// PmDirectoryPage — /pm-directory staff CRUD for the PM Company Directory
// (Phase 4, design §e/§9). Staff-managed list of local property-management
// firms whose verification inbox we KNOW, so the watcher can override the
// (often personal/stale) email an applicant types for a known firm.
//
// Single page: list table + "New entry" dialog + inline edit + delete confirm.
// Mirrors HoaListPage's dialog/confirm patterns and the DS .modal / .table /
// .field / .btn primitives.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  listPmDirectory,
  createPmEntry,
  updatePmEntry,
  deletePmEntry,
  type PmDirectoryRecord,
  type PmDirectoryCreate,
  type PmDirectoryPatch,
} from '@/lib/pmDirectoryClient';
import { ApiError } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Split a comma/space/newline-delimited textarea into a clean string array. */
function splitList(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function PmDirectoryPage() {
  const [entries, setEntries] = useState<PmDirectoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showNew, setShowNew] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PmDirectoryRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await listPmDirectory());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load PM directory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePmEntry(deleteTarget.pmId);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-7)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            PM directory
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
            Known property-management firms. Verification emails for a matching firm are redirected to the override inbox.
          </p>
        </div>
        <button type="button" className="btn" onClick={() => setShowNew(true)}>
          New entry
        </button>
      </div>

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
          No PM firms yet. Click "New entry" to add one.
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Override email</th>
                <th>Domains</th>
                <th>Phones</th>
                <th>Status</th>
                <th className="col-action" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.pmId}>
                  <td style={{ fontWeight: 'var(--weight-medium)' }}>
                    {e.companyName}
                    {e.isWelcomeHome && (
                      <span className="badge" data-size="sm" style={{ marginLeft: 'var(--space-2)' }}>WHP</span>
                    )}
                  </td>
                  <td>{e.overrideEmail}</td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                    {e.domains.length ? e.domains.join(', ') : '—'}
                  </td>
                  <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                    {e.phones?.length ? e.phones.join(', ') : '—'}
                  </td>
                  <td>
                    {e.active ? (
                      <span className="pill" data-tone="success">Active</span>
                    ) : (
                      <span className="pill" data-tone="neutral"><span className="dot" data-tone="muted" />Inactive</span>
                    )}
                  </td>
                  <td className="col-action">
                    <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                      <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={() => setEditId(e.pmId)}>
                        Edit
                      </button>
                      <button type="button" className="btn" data-variant="danger-ghost" data-size="sm" onClick={() => { setDeleteTarget(e); setDeleteError(null); }}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <PmEntryDialog
          mode="create"
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); void load(); }}
        />
      )}

      {editId && (
        <PmEntryDialog
          mode="edit"
          existing={entries.find((e) => e.pmId === editId) ?? null}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); void load(); }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteError(null); } }}
        title={deleteTarget ? `Delete ${deleteTarget.companyName}?` : 'Delete entry?'}
        description="This removes the firm from the directory. Verification emails will no longer be redirected to its override inbox."
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={deleting}
        error={deleteError}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / edit dialog
// ---------------------------------------------------------------------------

function PmEntryDialog({
  mode,
  existing,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  existing?: PmDirectoryRecord | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element | null {
  const containerRef = useFocusTrap(true);
  const firstRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState(existing?.companyName ?? '');
  const [overrideEmail, setOverrideEmail] = useState(existing?.overrideEmail ?? '');
  const [domains, setDomains] = useState((existing?.domains ?? []).join(', '));
  const [aliases, setAliases] = useState((existing?.aliases ?? []).join(', '));
  const [phones, setPhones] = useState((existing?.phones ?? []).join(', '));
  const [active, setActive] = useState(existing?.active ?? true);
  const [isWelcomeHome, setIsWelcomeHome] = useState(existing?.isWelcomeHome ?? false);
  const [owner, setOwner] = useState(existing?.owner ?? '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(onClose, true);
  useInertBackground(true);
  useEffect(() => { firstRef.current?.focus(); }, []);

  const emailValid = EMAIL_RE.test(overrideEmail.trim());
  const canSave = companyName.trim().length > 0 && emailValid && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        const body: PmDirectoryCreate = {
          companyName: companyName.trim(),
          overrideEmail: overrideEmail.trim(),
          domains: splitList(domains),
          active,
        };
        const al = splitList(aliases);
        const ph = splitList(phones);
        if (al.length) body.aliases = al;
        if (ph.length) body.phones = ph;
        if (isWelcomeHome) body.isWelcomeHome = true;
        if (owner.trim()) body.owner = owner.trim();
        await createPmEntry(body);
      } else if (existing) {
        // Send the full set of editable fields so cleared lists persist as empty.
        const patch: PmDirectoryPatch = {
          companyName: companyName.trim(),
          overrideEmail: overrideEmail.trim(),
          domains: splitList(domains),
          aliases: splitList(aliases),
          phones: splitList(phones),
          active,
          isWelcomeHome,
        };
        if (owner.trim()) patch.owner = owner.trim();
        await updatePmEntry(existing.pmId, patch);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        const b = err.body as { error?: string } | null;
        setError(b?.error ?? `Save failed (${err.status}).`);
      } else {
        setError(err instanceof Error ? err.message : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  }

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={() => { if (!saving) onClose(); }} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="pm-dialog-title" className="modal" data-size="md">
        <div className="modal-header">
          <h2 id="pm-dialog-title" className="modal-title">{mode === 'create' ? 'New PM firm' : 'Edit PM firm'}</h2>
        </div>
        <div className="modal-body">
          <div className="stack">
            <div className="field">
              <label htmlFor="pm-company" className="label">Company name</label>
              <input id="pm-company" ref={firstRef} className="input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Riverside Property Management" />
            </div>
            <div className="field">
              <label htmlFor="pm-email" className="label">Override email</label>
              <input id="pm-email" type="email" className="input" value={overrideEmail} aria-invalid={overrideEmail.length > 0 && !emailValid} onChange={(e) => setOverrideEmail(e.target.value)} placeholder="verify@firm.com" />
              {overrideEmail.length > 0 && !emailValid && (
                <div className="helper" data-state="error">Enter a valid email address.</div>
              )}
            </div>
            <div className="field">
              <label htmlFor="pm-domains" className="label">Domains</label>
              <input id="pm-domains" className="input" value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="firm.com, firmpm.com" />
              <div className="helper">Comma- or space-separated. Matched against the applicant-typed email domain.</div>
            </div>
            <div className="field">
              <label htmlFor="pm-aliases" className="label">Aliases</label>
              <input id="pm-aliases" className="input" value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="Riverside, Riverside PM" />
            </div>
            <div className="field">
              <label htmlFor="pm-phones" className="label">Phones</label>
              <input id="pm-phones" className="input" value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="509-555-1212" />
            </div>
            <div className="field">
              <label htmlFor="pm-owner" className="label">Owner / steward</label>
              <input id="pm-owner" className="input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who verified this firm" />
            </div>
            <div className="cluster" style={{ gap: 'var(--space-5)' }}>
              <label className="cluster" style={{ gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                <span>Active</span>
              </label>
              <label className="cluster" style={{ gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={isWelcomeHome} onChange={(e) => setIsWelcomeHome(e.target.checked)} />
                <span>This is Welcome Home (no auto-send)</span>
              </label>
            </div>
            {error && (
              <div className="alert" data-tone="danger">
                <div className="alert-body">{error}</div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn" onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}
